import { assertUnreachable } from '@sgme/fp';

import {
  type GroupQuoteLeg,
  sumGroupGreek,
  sumGroupMarkup,
  sumGroupPremium,
} from '@/App/tiles/Option/strategy/groupedPricing.ts';
import type { StrategyField } from '@/App/tiles/Option/strategy/types.ts';
import type { PremiumType } from '@/store/option/state.ts';

export type GroupPricingContext = {
  legs: GroupQuoteLeg[];
  premiumType: PremiumType;
  markupCurrencyIndex: PremiumType['premiumCurrency'] | undefined;
  showPricing: boolean;
  isInternalSales: boolean;
};

export function getPremiumSuffix(premiumType: PremiumType, currency: string) {
  switch (premiumType.premiumCategory) {
    case 'PERCENT':
      return ` % ${currency}`;
    case 'PPS':
      return ` Pps ${currency}`;
    case 'AMOUNT':
    case 'VOLATILITY':
      return ` ${currency}`;
    default:
      return assertUnreachable(premiumType.premiumCategory, `Unknown premium category: ${premiumType.premiumCategory}`);
  }
}

export function getMarkupCurrencyIndex(currency: string | undefined, currency1: string, currency2: string) {
  if (currency === currency1) {
    return 1 as const;
  }
  return currency === currency2 ? (2 as const) : undefined;
}

export function getSummaryValue(props: GroupPricingContext & { field: StrategyField }) {
  if (!props.showPricing) {
    return undefined;
  }
  const { field, legs, premiumType } = props;
  if (field === 'premium') {
    // Streamed Buy-ask / Sell-bid premiums only; manual per-leg overrides do not enter group totals.
    return sumGroupPremium(legs, premiumType);
  }
  if (field === 'markup' && props.isInternalSales) {
    return props.markupCurrencyIndex === undefined ? undefined : sumGroupMarkup(legs, props.markupCurrencyIndex);
  }
  return isGreekField(field) ? sumGroupGreek(legs, field) : undefined;
}

export function isGreekField(field: StrategyField): field is 'delta' | 'vega' | 'gamma' | 'theta' {
  return field === 'delta' || field === 'vega' || field === 'gamma' || field === 'theta';
}
